import { Navigate, useLocation } from 'react-router-dom';
import { useOrganization } from 'contexts/OrganizationContext';
import { isBlank, resolveScopeFallback, scopeForPath, orgNameFromPath } from 'utils/scopeGuard';

/**
 * Route guard for scope-scoped screens. Wrapped once around the layout Outlet, so it covers
 * every scoped route (and future ones) from a single place.
 *
 * The required scope is inferred from the URL (scopeForPath); pass `requiredScope` to override.
 * If the level's id is missing (scope not resolved), redirect up to the nearest resolved
 * ancestor (else MSP home) instead of rendering a page that would fire an empty-scope request
 * (52013 / "Failed to load data").
 *
 * Ids are read from localStorage, which NavGroup keeps as the source of truth. scopeResolved
 * gates the decision so a user whose context is still hydrating is never bounced. Non-scoped
 * routes ('0') pass straight through, with no gate, so MSP/onboarding/etc. are never touched.
 */
export default function ScopeGuard({ requiredScope, children }) {
  const location = useLocation();
  const { scopeResolved, recoverScopeFromRoute } = useOrganization();

  const scope = requiredScope != null ? String(requiredScope) : scopeForPath(location.pathname);

  // Non-scoped route: never guard, never gate.
  if (scope === '0') return children;

  // While scope is still settling, render the page rather than blanking the whole content
  // area. We only make the redirect decision once scope is resolved (so a valid user is
  // never bounced early), but an unresolved scope must NEVER leave the routed body blank.
  // (scopeResolved can lag on a direct-to-location login because persistPostLoginScope
  // writes localStorage without going through the context setters.)
  if (!scopeResolved) return children;

  const routeOrg = orgNameFromPath(location.pathname);
  let orgId = localStorage.getItem('OrgId');
  const currentNetwork = localStorage.getItem('currentNetwork');
  const currentDeviceGrpId = localStorage.getItem('currentDeviceGrpId');

  // Option D: org deep-link with empty orgId -> try to recover from the URL before bouncing.
  if (scope === '1' && isBlank(orgId) && !isBlank(routeOrg)) {
    orgId = recoverScopeFromRoute?.(routeOrg) || orgId;
  }

  // Cascade up to the nearest resolved ancestor (or stay if this level is resolved).
  const target = resolveScopeFallback({
    requiredScope: scope,
    orgId,
    orgName: routeOrg ?? localStorage.getItem('savedOrgName'),
    currentNetwork,
    currentDeviceGrpId
  });

  if (target && target !== location.pathname) {
    return <Navigate to={target} replace />;
  }
  return children;
}
